import { Component, forwardRef, inject, OnDestroy } from '@angular/core';
import { 
  ControlValueAccessor, 
  NG_VALUE_ACCESSOR, 
  NG_VALIDATORS,
  FormBuilder, 
  FormGroup, 
  Validators,
  AbstractControl,
  ValidationErrors,
  Validator,
  ReactiveFormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@shared/shared.module';
import { Subscription } from 'rxjs';
import { SignupRegistrationData } from '@shared/interfaces/onboarding-saga.interface';
import { RouterLink } from '@angular/router';


@Component({
  selector: 'app-signup-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule, RouterLink],
  providers: [
    // Register as NG_VALUE_ACCESSOR so Angular knows this is a form control
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SignupRegisterComponent),
      multi: true
    },
    // Register as NG_VALIDATORS so Angular can validate this control
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SignupRegisterComponent),
      multi: true
    }
  ],
  templateUrl: './signup-register.component.html',
  styleUrls: ['./signup-register.component.scss']
})
export class SignupRegisterComponent implements ControlValueAccessor, Validator, OnDestroy {
  private readonly fb: FormBuilder = inject(FormBuilder);

  private readonly subscription: Subscription;
  public form: FormGroup;

  // These callbacks are registered by Angular
  private onChange: (value: SignupRegistrationData | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      privacy_policy: [false, Validators.requiredTrue],
      terms: [false, Validators.requiredTrue],
    });

    // Subscribe to internal form changes and propagate to parent
    this.subscription = this.form.valueChanges.subscribe(value => {
      this.onChange(this.form.valid ? value : null);
    });

    // Mark as touched when any field is touched
    this.subscription.add(
      this.form.statusChanges.subscribe(() => {
        if (this.form.touched) {
          this.onTouched();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  // Called when Angular wants to write a value to this component
  // Example: parent does formGroup.patchValue({ registration: {...} })
  writeValue(value: SignupRegistrationData | null): void {
    if (value) {
      this.form.setValue(value, { emitEvent: false });
    } else {
      this.form.reset({}, { emitEvent: false });
    }
  }

  // Angular calls this to register the callback that should be called
  // when the component's value changes
  registerOnChange(fn: (value: SignupRegistrationData | null) => void): void {
    this.onChange = fn;
  }

  // Angular calls this to register the callback that should be called
  // when the component is "touched" (blurred)
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Angular calls this when the control's disabled state should change
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  // Called by Angular to validate this control
  // Return null if valid, or an error object if invalid
  validate(control: AbstractControl): ValidationErrors | null {
    if (this.form.valid) {
      return null;
    }
    
    // Return errors from internal form
    return {
      registrationForm: {
        valid: false,
        errors: this.getFormErrors()
      }
    };
  }

  // Helper to collect all errors from internal form
  private getFormErrors(): any {
    const errors: any = {};
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    if (this.form.errors) {
      errors._form = this.form.errors;
    }
    return errors;
  }
}
